import React, { useEffect, useState } from 'react';
import {
  ArrowDownLeft,
  ArrowUpRight,
  LayoutDashboard,
  RefreshCw,
  Users,
  Wallet,
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { adminService } from '../services/adminService';
import { getApiErrorMessage } from '../utils/apiError';
import { formatMoney } from '../utils/formatMoney';

function StatCard({ label, value, hint, accent = false }) {
  return (
    <Card className={accent ? 'border-2 border-brand-red/20' : ''}>
      <CardContent className="space-y-1 pt-6">
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-3xl font-bold text-brand-red tabular-nums">
          {formatMoney(value ?? 0)}
        </p>
        {hint ? <p className="text-xs text-gray-500">{hint}</p> : null}
      </CardContent>
    </Card>
  );
}

export function AdminDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminService
      .getDashboard()
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getApiErrorMessage(err, 'โหลด Dashboard ไม่สำเร็จ'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return (
    <div className="space-y-6">
      {error ? (
        <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-brand-red">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-bold text-brand-red">
            <LayoutDashboard className="h-5 w-5" />
            Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            สรุปเงินเข้า–ออก และสถานะระบบสำหรับผู้ดูแล
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={loading}
          onClick={() => setRefreshKey((key) => key + 1)}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          รีเฟรช
        </Button>
      </div>

      <Card className="border-2 border-brand-gold/30">
        <CardContent className="space-y-1 pt-6">
          <p className="text-sm font-semibold text-brand-gold">
            รอบคำนวณจริง — เริ่ม{' '}
            {data?.statsSince
              ? new Date(data.statsSince).toLocaleString('th-TH')
              : '27 ส.ค. 2026 00:00'}
          </p>
          <p className="text-xs text-gray-500">
            นับเฉพาะรายการหลังวันเริ่มต้น — ยอด mock เก่าในบัญชีผู้ใช้ยังอยู่
            แต่ไม่รวมในสรุปฝาก/ถอน/คอมของรอบนี้
          </p>
        </CardContent>
      </Card>

      <section className="space-y-3">
        <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500">
          เงินเข้า / เงินออก (รอบจริง)
        </h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="เงินเข้า — ฝาก USDT"
            value={data?.moneyIn?.deposits}
            hint={`${data?.moneyIn?.depositCount ?? 0} รายการสำเร็จ`}
            accent
          />
          <StatCard
            label="เงินออก — ถอน USDT"
            value={data?.moneyOut?.withdrawals}
            hint={`${data?.moneyOut?.withdrawalCount ?? 0} รายการ (สำเร็จ/รอดำเนินการ)`}
          />
          <StatCard
            label="เงินออก — คอมมิชชัน"
            value={data?.moneyOut?.commissions}
            hint={`${data?.moneyOut?.commissionCount ?? 0} รายการ`}
          />
          <StatCard
            label="เงินออก — จ่ายรางวัล"
            value={data?.moneyOut?.payouts}
            hint="ลอตเตอรี่ + ยี่กี + สอยดาว"
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Card className="border-2 border-green-200">
            <CardContent className="space-y-1 pt-6">
              <p className="flex items-center gap-2 text-sm text-green-700">
                <ArrowDownLeft className="h-4 w-4" />
                Net เงินเข้าจากนอกระบบ
              </p>
              <p className="text-3xl font-bold text-green-700 tabular-nums">
                {formatMoney(data?.netExternalCashflow ?? 0)}
              </p>
              <p className="text-xs text-gray-500">ฝาก − ถอน</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="space-y-1 pt-6">
              <p className="flex items-center gap-2 text-sm text-gray-500">
                <ArrowUpRight className="h-4 w-4" />
                หลังหักคอมมิชชัน
              </p>
              <p className="text-3xl font-bold text-brand-red tabular-nums">
                {formatMoney(data?.netAfterCommission ?? 0)}
              </p>
              <p className="text-xs text-gray-500">ฝาก − ถอน − คอม</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="space-y-1 pt-6">
              <p className="text-sm text-gray-500">ยอดเดิมพันรวม (รอบจริง)</p>
              <p className="text-3xl font-bold text-brand-red tabular-nums">
                {formatMoney(data?.periodBetVolume ?? 0)}
              </p>
              <p className="text-xs text-gray-500">
                ลอตโต้ {formatMoney(data?.periodBetBreakdown?.lottery ?? 0)} · หวยลาว{' '}
                {formatMoney(data?.periodBetBreakdown?.lao ?? 0)} · ยี่กี{' '}
                {formatMoney(data?.periodBetBreakdown?.yiki ?? 0)} · สอยดาว{' '}
                {formatMoney(data?.periodBetBreakdown?.soiDao ?? 0)}
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500">
          สถานะปัจจุบันทั้งระบบ
        </h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="border-2 border-brand-red/20">
            <CardContent className="space-y-1 pt-6">
              <p className="flex items-center gap-2 text-sm text-gray-500">
                <Wallet className="h-4 w-4 text-brand-red" />
                ยอดคงเหลือผู้ใช้รวม (ถอนได้)
              </p>
              <p className="text-3xl font-bold text-brand-red tabular-nums">
                {formatMoney(data?.totalBalance ?? 0)}
              </p>
              <p className="text-xs text-gray-500">
                หนี้สินต่อผู้ใช้รวมยอดเก่าในบัญชีด้วย
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="space-y-1 pt-6">
              <p className="flex items-center gap-2 text-sm text-gray-500">
                <Users className="h-4 w-4 text-brand-gold" />
                ผู้ใช้ / Sale
              </p>
              <p className="text-3xl font-bold text-brand-red">
                {data?.userCount ?? 0}
                <span className="text-lg font-semibold text-gray-500">
                  {' '}
                  / {data?.saleCount ?? 0} sale
                </span>
              </p>
            </CardContent>
          </Card>
          <Card className="border-2 border-brand-gold/30">
            <CardContent className="space-y-1 pt-6">
              <p className="text-sm text-gray-500">กระเป๋า Admin</p>
              <p className="text-3xl font-bold text-brand-gold tabular-nums">
                {formatMoney(data?.adminBalance ?? 0)}
              </p>
              <p className="text-xs text-gray-500">
                เข้า {formatMoney(data?.moneyIn?.adminPoolIn ?? 0)} · ออก{' '}
                {formatMoney(data?.moneyOut?.adminPoolOut ?? 0)} (รอบจริง)
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">กองรางวัล</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">รางวัลที่ 1</span>
                <span className="font-semibold tabular-nums">
                  {formatMoney(data?.pools?.first ?? 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">3 ตัว</span>
                <span className="font-semibold tabular-nums">
                  {formatMoney(data?.pools?.three ?? 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">2 ตัว</span>
                <span className="font-semibold tabular-nums">
                  {formatMoney(data?.pools?.two ?? 0)}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
